import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Check, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import './Select.css'

/**
 * Custom themed Select
 *
 * Props:
 *  - value        : current value (string | number)
 *  - onChange     : (value) => void      ← event nahi, direct value male chhe
 *  - options      : [{ value, label }]
 *  - placeholder  : text jyare kai selected na hoy
 *  - searchable   : true hoy to menu ma search box dekhay
 *  - disabled     : boolean
 *  - className    : trigger button par extra class
 *  - style        : trigger button par extra style
 *  - minMenuWidth : menu nu minimum width (px)
 *  - ariaLabel    : accessibility label
 *
 * Menu document.body ma portal thi render thay chhe, etle modal / overflow:hidden
 * / scrollable body ni andar pan clip thatu nathi.
 */
export default function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchable = false,
  disabled = false,
  className = '',
  style,
  minMenuWidth = 0,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [hl, setHl] = useState(-1)
  const [pos, setPos] = useState(null)

  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const listRef = useRef(null)
  const searchRef = useRef(null)

  const selected = options.find((o) => String(o.value) === String(value))
  const isPlaceholder = !selected || selected.value === '' || selected.value === null

  const filtered = useMemo(() => {
    if (!query.trim()) return options
    const q = query.toLowerCase()
    return options.filter((o) => String(o.label).toLowerCase().includes(q))
  }, [options, query])

  /* ── position (neeche jagya na hoy to upar khule) ── */
  const place = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const estimated = Math.min(280, filtered.length * 36 + (searchable ? 56 : 0) + 12)
    const spaceBelow = window.innerHeight - r.bottom
    const openUp = spaceBelow < estimated + 12 && r.top > spaceBelow
    const room = (openUp ? r.top : spaceBelow) - 16
    setPos({
      left: Math.max(8, Math.min(r.left, window.innerWidth - Math.max(r.width, minMenuWidth) - 8)),
      width: Math.max(r.width, minMenuWidth),
      top: openUp ? undefined : r.bottom + 6,
      bottom: openUp ? window.innerHeight - r.top + 6 : undefined,
      maxH: Math.max(140, Math.min(280, room - (searchable ? 56 : 0))),
      up: openUp,
    })
  }, [filtered.length, searchable, minMenuWidth])

  useLayoutEffect(() => {
    if (!open) return undefined
    place()
    const onMove = (e) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return
      place()
    }
    window.addEventListener('resize', onMove)
    window.addEventListener('scroll', onMove, true)
    return () => {
      window.removeEventListener('resize', onMove)
      window.removeEventListener('scroll', onMove, true)
    }
  }, [open, place])

  /* ── bahar click karo to band ── */
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return
      setOpen(false)
      setPos(null)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  /* ── open thay tyare search par focus ── */
  useEffect(() => {
    if (open && pos && searchable) searchRef.current?.focus({ preventScroll: true })
  }, [open, pos, searchable])

  /* ── highlighted item dekhay tya sudhi scroll ── */
  useEffect(() => {
    if (!open || hl < 0) return
    listRef.current?.querySelector(`[data-i="${hl}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [hl, open, pos])

  const openMenu = () => {
    if (disabled) return
    setQuery('')
    setHl(Math.max(0, options.findIndex((o) => String(o.value) === String(value))))
    setOpen(true)
  }

  const close = (refocus = false) => {
    setOpen(false)
    setPos(null)
    if (refocus) triggerRef.current?.focus()
  }

  const choose = (opt) => {
    if (!opt) return
    onChange?.(opt.value)
    close(true)
  }

  const move = (dir) => {
    if (!filtered.length) return
    setHl((h) => (h < 0 ? 0 : (h + dir + filtered.length) % filtered.length))
  }

  const onKey = (e) => {
    if (disabled) return
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault()
        openMenu()
      }
      return
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        close(true)
        break
      case 'ArrowDown':
        e.preventDefault()
        move(1)
        break
      case 'ArrowUp':
        e.preventDefault()
        move(-1)
        break
      case 'Home':
        if (!searchable) { e.preventDefault(); setHl(0) }
        break
      case 'End':
        if (!searchable) { e.preventDefault(); setHl(filtered.length - 1) }
        break
      case 'Enter':
        e.preventDefault()
        choose(filtered[hl])
        break
      case ' ':
        if (!searchable) { e.preventDefault(); choose(filtered[hl]) }
        break
      case 'Tab':
        close(false)
        break
      default:
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`sel-trigger ${open ? 'is-open' : ''} ${isPlaceholder ? 'is-placeholder' : ''} ${className}`}
        style={style}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onKey}
      >
        <span className="sel-value">{selected ? selected.label : placeholder}</span>
        <ChevronDown size={15} className="sel-chevron" />
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          className={`sel-menu ${pos.up ? 'is-up' : ''}`}
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
          onKeyDown={onKey}
        >
          {searchable && (
            <div className="sel-search">
              <Search size={13} />
              <input
                ref={searchRef}
                type="text"
                placeholder="Search…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setHl(0)
                }}
              />
            </div>
          )}

          <ul className="sel-list" role="listbox" ref={listRef} style={{ maxHeight: pos.maxH }}>
            {filtered.length === 0 && <li className="sel-empty">No results</li>}
            {filtered.map((o, i) => {
              const isSel = String(o.value) === String(value)
              return (
                <li
                  key={`${o.value}-${i}`}
                  data-i={i}
                  role="option"
                  aria-selected={isSel}
                  className={`sel-option ${isSel ? 'is-selected' : ''} ${i === hl ? 'is-hl' : ''}`}
                  onMouseEnter={() => setHl(i)}
                  onClick={() => choose(o)}
                >
                  <span className="sel-option-label">{o.label}</span>
                  {isSel && <Check size={14} />}
                </li>
              )
            })}
          </ul>
        </div>,
        document.body
      )}
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * Pagination (reusable) — rows-per-page Select sathe
 *
 * Props:
 *  - total              : total records (filtered)
 *  - page               : current page (1-based)
 *  - pageSize           : rows per page
 *  - onPageChange       : (page) => void
 *  - onPageSizeChange   : (size) => void
 *  - pageSizeOptions    : default [10, 20, 50, 100]
 * ═══════════════════════════════════════════════════════════════════ */
const buildPages = (page, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
  const pages = [1]
  const start = Math.max(2, page - 1)
  const end = Math.min(totalPages - 1, page + 1)
  if (start > 2) pages.push('…l')
  for (let i = start; i <= end; i += 1) pages.push(i)
  if (end < totalPages - 1) pages.push('…r')
  pages.push(totalPages)
  return pages
}

export function Pagination({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(page, totalPages)
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1
  const to = Math.min(current * pageSize, total)

  const sizeOptions = useMemo(
    () => pageSizeOptions.map((n) => ({ value: n, label: String(n) })),
    [pageSizeOptions]
  )

  const go = (p) => {
    const next = Math.min(Math.max(1, p), totalPages)
    if (next !== current) onPageChange?.(next)
  }

  return (
    <div className="pg-bar">
      <div className="pg-left">
        <span className="pg-info">
          Showing <b>{from}</b>–<b>{to}</b> of <b>{total}</b>
        </span>
        <div className="pg-size">
          <span>Rows per page</span>
          <Select
            className="pg-size-select"
            ariaLabel="Rows per page"
            value={pageSize}
            onChange={(v) => onPageSizeChange?.(Number(v))}
            options={sizeOptions}
            minMenuWidth={80}
          />
        </div>
      </div>

      <div className="pg-nav" role="navigation" aria-label="Pagination">
        <button type="button" className="pg-btn" onClick={() => go(1)} disabled={current === 1} aria-label="First page">
          <ChevronsLeft size={15} />
        </button>
        <button type="button" className="pg-btn" onClick={() => go(current - 1)} disabled={current === 1} aria-label="Previous page">
          <ChevronLeft size={15} />
        </button>

        {buildPages(current, totalPages).map((p) =>
          typeof p === 'string' ? (
            <span key={p} className="pg-dots">…</span>
          ) : (
            <button
              key={p}
              type="button"
              className={`pg-btn pg-num ${p === current ? 'is-active' : ''}`}
              onClick={() => go(p)}
              aria-current={p === current ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}

        <button type="button" className="pg-btn" onClick={() => go(current + 1)} disabled={current === totalPages} aria-label="Next page">
          <ChevronRight size={15} />
        </button>
        <button type="button" className="pg-btn" onClick={() => go(totalPages)} disabled={current === totalPages} aria-label="Last page">
          <ChevronsRight size={15} />
        </button>
      </div>
    </div>
  )
}